import { Button, Center, ColorInput, Divider, Fieldset, Group, Select, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { hex, wcagContrastValue } from 'a11y-color-contrast';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useMemo, useState } from 'react';
import type { z } from 'zod/v4';

import {
  InvoiceAppearanceSchema,
  type UpdateInvoiceAppearanceVariables,
  useLoadInvoiceAppearance,
  useUpdateInvoiceAppearance,
} from '../api';
import { LogoUploader } from './LogoUploader';

const DEFAULT_COLOR = '#0A0A0A';
const LIGHT_COLOR = '#ffffff';
const DARK_COLOR = '#000000';

const AppearanceFormSchema = InvoiceAppearanceSchema.omit({ logoUrl: true });
type AppearanceFormValues = z.infer<typeof AppearanceFormSchema>;

export const AppearanceCard = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const { invoiceAppearance } = useLoadInvoiceAppearance();
  const { updateInvoiceAppearance, isUpdateInvoiceAppearanceLoading } =
    useUpdateInvoiceAppearance();

  // The logo is a File (or a removal), not a plain form value, so it lives in
  // its own state: undefined = untouched, File = new upload, null = removal.
  const [logo, setLogo] = useState<File | null | undefined>(undefined);

  const form = useForm<AppearanceFormValues>({
    initialValues: { color: null, textColor: 'auto' },
    validate: schemaResolver(AppearanceFormSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded appearance into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!invoiceAppearance) return;

    const values = { color: invoiceAppearance.color, textColor: invoiceAppearance.textColor };
    form.setInitialValues(values);
    form.setValues(values);
    setLogo(undefined);
  }, [invoiceAppearance]);

  const isLogoDirty = logo !== undefined;

  const logoObjectUrl = useMemo(
    () => (logo instanceof File ? URL.createObjectURL(logo) : null),
    [logo],
  );

  useEffect(() => {
    if (!logoObjectUrl) return;

    return () => URL.revokeObjectURL(logoObjectUrl);
  }, [logoObjectUrl]);

  const logoPreview =
    logo === null ? undefined : (logoObjectUrl ?? invoiceAppearance?.logoUrl ?? undefined);

  const getReadableTextColor = () => {
    const ratio = wcagContrastValue(hex(form.getValues().color ?? DEFAULT_COLOR), hex(DARK_COLOR));

    return ratio > 4.5 ? DARK_COLOR : LIGHT_COLOR;
  };

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdateInvoiceAppearanceVariables = {};
    if (form.isDirty('color')) payload.color = values.color;
    if (form.isDirty('textColor')) payload.textColor = values.textColor;
    if (isLogoDirty) payload.logo = logo;

    updateInvoiceAppearance(payload, {
      onSuccess: () => {
        form.resetDirty();
        setLogo(undefined);
      },
    });
  };

  const { color, textColor } = form.getValues();
  const isDirty = form.isDirty() || isLogoDirty;

  return (
    <Panel
      title={t(i18nKeys.SETTINGS.INVOICING.APPEARANCE)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!isDirty}
          loading={isUpdateInvoiceAppearanceLoading}
          hideIcon
        />
      }
    >
      <Group align="start" my="md">
        <LogoUploader
          flex={1}
          h="100%"
          previewUrl={logoPreview}
          onSelect={file => setLogo(file)}
          onRemove={() => setLogo(null)}
        />
        <Divider orientation="vertical" mx="xl" />
        <Stack flex={1}>
          <ColorInput
            popoverProps={{ position: 'right-start' }}
            label={t(i18nKeys.FORM.INVOICING.YOUR_COLOR)}
            {...form.getInputProps('color')}
            value={color ?? DEFAULT_COLOR}
          />
          <Select
            label={t(i18nKeys.FORM.INVOICING.YOUR_TEXT_COLOR)}
            data={[
              { value: 'auto', label: t(i18nKeys.FORM.INVOICING.AUTO) },
              { value: DARK_COLOR, label: t(i18nKeys.FORM.INVOICING.BLACK) },
              { value: LIGHT_COLOR, label: t(i18nKeys.FORM.INVOICING.WHITE) },
            ]}
            allowDeselect={false}
            {...form.getInputProps('textColor')}
          />
          <Fieldset legend={t(i18nKeys.FORM.INVOICING.PREVIEW)}>
            <Center>
              <Button
                color={color ?? DEFAULT_COLOR}
                c={textColor === 'auto' ? getReadableTextColor() : textColor}
                style={{ pointerEvents: 'none' }}
              >
                {company.name}
              </Button>
            </Center>
          </Fieldset>
        </Stack>
      </Group>
    </Panel>
  );
};
